import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterPhoDistrictHomesPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster Phở District Homes | Live Near the Best Phở | Truc Doan</title>
        <meta name="description" content="Find homes near Westminster's famous Phở District on Bolsa Avenue. Live steps away from the best Vietnamese restaurants in Little Saigon. Truc Doan, your local Westminster realtor." />
        <meta name="keywords" content="Westminster Pho District homes, Bolsa Ave homes for sale, Little Saigon real estate, Westminster Vietnamese restaurants, homes near Pho restaurants Westminster, Truc Doan realtor, Westminster CA homes" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-pho-district-homes`} />
        <meta property="og:title" content="Westminster Phở District Homes | Live Near the Best Phở | Truc Doan" />
        <meta property="og:description" content="Live near Westminster's famous Phở restaurants on Bolsa Ave. Explore homes in the heart of Little Saigon's culinary scene." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-pho-district-homes`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/westminster-pho-district-homes`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Find homes near Westminster's famous Phở District. Truc Doan helps you live near the best Vietnamese culinary scene in Orange County.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster Phở District Homes for Sale</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Live steps away from the best Phở in Southern California — right in the heart of Little Saigon's culinary corridor.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Life in Westminster's Phở District</h2>
          <p>Westminster's Bolsa Avenue is world-renowned for its concentration of authentic Vietnamese Phở restaurants. From steaming bowls of phở tái to specialty bún bò Huế, this corridor offers an unmatched culinary experience that draws food lovers from across Southern California.</p>
          <p>Living near the Phở District means walkable access to dozens of restaurants, bakeries, and cafés. Residents enjoy the vibrant energy of Little Saigon while benefiting from Westminster's quiet residential neighborhoods just blocks from all the action.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Why Live Near Westminster's Phở District</h2>
          <p>Homes near the Phở District offer the perfect blend of culinary culture and suburban comfort. Here's what makes this area special:</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>World-Class Phở</h3>
              <p>Over 30 acclaimed Phở restaurants within walking distance, including Phở 79, Phở Quang Trung, and Phở Dakao — open early morning to late night.</p>
            </div>
            <div className="amenity">
              <h3>Walkable Dining</h3>
              <p>No need to drive — step out your door and enjoy authentic Vietnamese cuisine, from bánh mì shops to elegant seafood restaurants.</p>
            </div>
            <div className="amenity">
              <h3>Vibrant Night Life</h3>
              <p>The Bolsa corridor comes alive in the evenings with cafés, dessert shops, and late-night eateries catering to all ages.</p>
            </div>
            <div className="amenity">
              <h3>Cultural Immersion</h3>
              <p>Beyond food, enjoy Vietnamese grocery stores, herbal medicine shops, bookstores, and cultural centers all along Bolsa Avenue.</p>
            </div>
            <div className="amenity">
              <h3>Community Events</h3>
              <p>Tết festivals, Mid-Autumn celebrations, and food fairs happen regularly along Bolsa Avenue, creating a strong sense of community.</p>
            </div>
            <div className="amenity">
              <h3>Convenient Location</h3>
              <p>Quick access to the 405 and 22 freeways, with shopping centers, parks, and top-rated schools nearby.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Phở District Market Snapshot</h2>
          <p>The Phở District area remains highly desirable for buyers who value walkability to dining and cultural amenities.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>~$740,000</td></tr>
              <tr><td>Avg Days on Market</td><td>18–28 Days</td></tr>
              <tr><td>Most Active Price Range</td><td>$550K – $880K</td></tr>
              <tr><td>Property Types</td><td>Single Family, Condos, Townhomes</td></tr>
              <tr><td>Neighborhood Vibe</td><td>Walkable, Cultural, Food-Centric</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Homes for Sale Near Westminster's Phở District</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Loading properties...</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>Listed by: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available Westminster Phở District listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Live Near Westminster's Best Phở?</h2>
          <p>Contact Truc Doan today for a free consultation about homes near the Phở District.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Westminster Pho District Homes Inquiry" className="cta-button primary">Email Truc Doan</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterPhoDistrictHomesPage;
